import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { ContractService } from '@/services/contract.service';
import { ContractType, ContractStatus } from '@/models/contract.model';
import { format } from 'date-fns';

export const Contracts: React.FC = () => {
  const { profile } = useAuth();
  const [typeFilter, setTypeFilter] = useState<'all' | ContractType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | ContractStatus>('all');

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ['contracts', typeFilter, statusFilter],
    queryFn: async () => {
      const all = await ContractService.getAllContracts();
      let filtered = all;
      if (typeFilter !== 'all') filtered = filtered.filter(c => c.type === typeFilter);
      if (statusFilter !== 'all') filtered = filtered.filter(c => c.status === statusFilter);
      return filtered;
    },
    enabled: !!profile,
  });

  const { data: expiringContracts = [] } = useQuery({
    queryKey: ['expiringContracts'],
    queryFn: () => ContractService.getExpiringContracts(90),
    enabled: !!profile,
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      terminated: 'bg-red-100 text-red-800',
      renewed: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const activeContracts = contracts.filter(c => c.status === ContractStatus.active).length;
  const totalValue = contracts.filter(c => c.status === ContractStatus.active).reduce((sum, c) => sum + c.value, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Contract Management</h1>
        <p className="mt-2 text-gray-600">Manage contracts, agreements, and renewals</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Contracts</div>
          <div className="text-2xl font-bold text-gray-900">{contracts.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Active Contracts</div>
          <div className="text-2xl font-bold text-green-600">{activeContracts}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Value</div>
          <div className="text-2xl font-bold text-blue-600">${totalValue.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Expiring Soon</div>
          <div className="text-2xl font-bold text-orange-600">{expiringContracts.length}</div>
        </div>
      </div>

      {expiringContracts.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-orange-800">
                {expiringContracts.length} contract(s) expiring in the next 90 days
              </h3>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm"
            >
              <option value="all">All Types</option>
              {Object.values(ContractType).map(type => (
                <option key={type} value={type}>{type.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm"
            >
              <option value="all">All Statuses</option>
              {Object.values(ContractStatus).map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Link
              to="/contracts/new"
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium text-center"
            >
              + Create Contract
            </Link>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : contracts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">No contracts found</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {contracts.map((contract) => (
              <li key={contract.id}>
                <Link
                  to={`/contracts/${contract.id}`}
                  className="block hover:bg-gray-50 px-6 py-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{contract.title}</p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                          {contract.status}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">{contract.type}</span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        {contract.partyName} • {contract.contractNumber}
                        {contract.endDate && ` • Expires: ${format(contract.endDate, 'MMM d, yyyy')}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">${contract.value.toLocaleString()}</p>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

